import {
  colorVariants,
  extendVariants,
  Chip as NextUIChip,
} from "@heroui/react";
import clsx from "clsx";
import { forwardRef, LegacyRef, ReactElement } from "react";

const ExtendedChip = extendVariants(NextUIChip, {
  slots: {
    base: [
      "relative",
      "max-w-fit",
      "min-w-min",
      "inline-flex",
      "items-center",
      "justify-between",
      "box-border",
      "whitespace-nowrap",
    ],
    content: "flex-1 text-gray-900 font-normal",
    dot: ["w-2", "h-2", "ml-1", "rounded-full"],
    avatar: ["flex-shrink-0", "h-4"],
  },
  variants: {
    variant: {
      solid: {
        base: ["text-gray-900"],
      },
      bordered: {
        base: ["border-medium"],
      },
      light: {
        base: ["bg-transparent"],
      },
      flat: {},
      faded: {
        base: ["border-medium"],
      },
      shadow: {},
      dot: {
        base: [
          "border-medium",
          "border-default",
          "text-foreground",
          "border-gray-500",
        ],
        content: ["!pr-0"],
      },
    },
    color: {
      default: {
        dot: ["bg-default-400"],
      },
      white: {
        base: ["bg-white"],
        dot: ["bg-white"],
      },
      black: {
        base: ["bg-black text-white"],
        dot: ["bg-black"],
      },
      gray: {
        base: ["bg-gray-400"],
        dot: ["bg-gray-500"],
      },
      primary: {
        dot: ["bg-primary"],
      },
      secondary: {
        dot: ["bg-secondary"],
      },
      accent: {
        base: ["bg-accent-dark text-white"],
        dot: ["bg-accent-dark"],
      },
      success: {
        dot: ["bg-success"],
      },
      warning: {
        dot: ["bg-warning"],
      },
      danger: {
        dot: ["bg-danger"],
      },
    },
    size: {
      sm: {
        base: ["px-2", "py-1", "h-7"],
        content: ["px-1 text-small"],
      },
      md: {
        base: ["px-3", "py-1.5", "h-8"],
        content: ["px-2 text-small"],
      },
      lg: {
        base: ["px-3", "py-2", "h-9"],
        content: ["px-2 text-small"],
      },
    },
    radius: {
      none: {
        base: ["rounded-none"],
      },
      sm: {
        base: ["rounded-small"],
      },
      md: {
        base: ["rounded-medium"],
      },
      lg: {
        base: ["rounded-large"],
      },
      full: {
        base: ["rounded-full"],
      },
    },
    isOneChar: {
      true: {},
      false: {},
    },
    isCloseable: {
      true: {},
      false: {},
    },
    hasStartContent: {
      true: {},
    },
    hasEndContent: {
      true: {},
    },
    isDisabled: {
      true: { base: ["opacity-disabled", "pointer-events-none"] },
    },
  },
  defaultVariants: {
    variant: "solid",
    color: "default",
    size: "md",
    radius: "full",
    isDisabled: false,
  },
  compoundVariants: [
    // solid / color
    {
      variant: "solid",
      color: "default",
      class: [colorVariants.solid.default],
    },
    {
      variant: "solid",
      color: "primary",
      class: [colorVariants.solid.primary],
    },
    {
      variant: "solid",
      color: "secondary",
      class: [colorVariants.solid.secondary],
    },
    {
      variant: "solid",
      color: "success",
      class: [colorVariants.solid.success],
    },
    {
      variant: "solid",
      color: "warning",
      class: [colorVariants.solid.warning],
    },
    {
      variant: "solid",
      color: "danger",
      class: [colorVariants.solid.danger],
    },
    // shadow / color
    {
      variant: "shadow",
      color: "default",
      class: [colorVariants.shadow.default],
    },
    {
      variant: "shadow",
      color: "primary",
      class: [colorVariants.shadow.primary],
    },
    {
      variant: "shadow",
      color: "secondary",
      class: [colorVariants.shadow.secondary],
    },
    {
      variant: "shadow",
      color: "success",
      class: [colorVariants.shadow.success],
    },
    {
      variant: "shadow",
      color: "warning",
      class: [colorVariants.shadow.warning],
    },
    {
      variant: "shadow",
      color: "danger",
      class: [colorVariants.shadow.danger],
    },
    // bordered / color
    {
      variant: "bordered",
      color: "white",
      class: ["border-white", "text-white", "bg-transparent"],
    },
    {
      variant: "bordered",
      color: "black",
      class: ["border-black", "text-base-900", "bg-transparent"],
    },
    {
      variant: "bordered",
      color: "gray",
      class: ["border-gray-500", "text-gray-900", "bg-transparent"],
    },
    {
      variant: "bordered",
      color: "default",
      class: [colorVariants.bordered.default, "bg-transparent"],
    },
    {
      variant: "bordered",
      color: "primary",
      class: [
        colorVariants.bordered.primary,
        "border-primary",
        "text-primary",
        "bg-transparent",
      ],
    },
    {
      variant: "bordered",
      color: "secondary",
      class: [
        colorVariants.bordered.secondary,
        "border-secondary",
        "text-secondary",
        "bg-transparent",
      ],
    },
    {
      variant: "bordered",
      color: "accent",
      class: ["border-accent-dark", "text-accent-dark", "bg-transparent"],
    },
    {
      variant: "bordered",
      color: "success",
      class: [
        colorVariants.bordered.success,
        "border-success",
        "text-success",
        "bg-transparent",
      ],
    },
    {
      variant: "bordered",
      color: "warning",
      class: [
        colorVariants.bordered.warning,
        "border-warning-dark",
        "text-warning-dark",
        "bg-transparent",
      ],
    },
    {
      variant: "bordered",
      color: "danger",
      class: [
        colorVariants.bordered.danger,
        "border-danger",
        "text-danger",
        "bg-transparent",
      ],
    },
    // flat / color
    {
      variant: "flat",
      color: "default",
      class: [colorVariants.flat.default],
    },
    {
      variant: "flat",
      color: "white",
      class: [],
    },
    {
      variant: "flat",
      color: "black",
      class: [],
    },
    {
      variant: "flat",
      color: "gray",
      class: [],
    },
    {
      variant: "flat",
      color: "primary",
      class: [colorVariants.flat.primary, "bg-blue-100", "text-primary-dark"],
    },
    {
      variant: "flat",
      color: "secondary",
      class: [
        colorVariants.flat.secondary,
        "bg-secondary-light",
        "text-yellow-900",
      ],
    },
    {
      variant: "flat",
      color: "accent",
      class: ["bg-accent-light", "text-purple-800"],
    },
    {
      variant: "flat",
      color: "success",
      class: ["bg-success-light", "text-green-700"],
    },
    {
      variant: "flat",
      color: "warning",
      class: [
        colorVariants.flat.warning,
        "bg-warning-light",
        "text-orange-800",
      ],
    },
    {
      variant: "flat",
      color: "danger",
      class: [colorVariants.flat.danger, "bg-danger-light", "text-red-900"],
    },
    // faded / color
    {
      variant: "faded",
      color: "default",
      class: [colorVariants.faded.default, "border-gray-500"],
    },
    {
      variant: "faded",
      color: "white",
      class: [
        colorVariants.faded.default,
        "bg-gray-900",
        "text-white",
        "border-gray-500",
      ],
    },
    {
      variant: "faded",
      color: "black",
      class: ["bg-gray-50", "text-base-900", "border-gray-500"],
    },
    {
      variant: "faded",
      color: "gray",
      class: ["bg-gray-50", "text-gray-600", "border-gray-500"],
    },
    {
      variant: "faded",
      color: "primary",
      class: [
        colorVariants.faded.primary,
        "bg-gray-50",
        "text-primary",
        "border-gray-500",
      ],
    },
    {
      variant: "faded",
      color: "secondary",
      class: [
        colorVariants.faded.secondary,
        "bg-gray-900",
        "text-secondary-dark",
        "border-gray-500",
      ],
    },
    {
      variant: "faded",
      color: "accent",
      class: ["bg-gray-50", "text-accent-dark", "border-gray-500"],
    },
    {
      variant: "faded",
      color: "success",
      class: [
        colorVariants.faded.success,
        "bg-gray-50",
        "text-success",
        "border-gray-500",
      ],
    },
    {
      variant: "faded",
      color: "warning",
      class: [
        colorVariants.faded.warning,
        "bg-gray-50",
        "text-warning-dark",
        "border-gray-500",
      ],
    },
    {
      variant: "faded",
      color: "danger",
      class: [
        colorVariants.faded.danger,
        "bg-gray-50",
        "text-danger-dark",
        "border-gray-500",
      ],
    },
    // light / color
    {
      variant: "light",
      color: "default",
      class: [colorVariants.light.default],
    },
    {
      variant: "light",
      color: "primary",
      class: [colorVariants.light.primary],
    },
    {
      variant: "light",
      color: "secondary",
      class: [colorVariants.light.secondary],
    },
    {
      variant: "light",
      color: "success",
      class: [colorVariants.light.success],
    },
    {
      variant: "light",
      color: "warning",
      class: [colorVariants.light.warning],
    },
    {
      variant: "light",
      color: "danger",
      class: [colorVariants.light.danger],
    },
    // dot / color
    {
      variant: "dot",
      color: "default",
      class: ["bg-transparent"],
    },
    {
      variant: "dot",
      color: "white",
      class: ["text-white", "border-white", "bg-transparent"],
    },
    {
      variant: "dot",
      color: "black",
      class: ["text-gray-900", "bg-transparent"],
    },
    {
      variant: "dot",
      color: "gray",
      class: ["text-gray-900", "bg-transparent"],
    },
    {
      variant: "dot",
      color: "primary",
      class: ["text-gray-900", "bg-transparent"],
    },
    {
      variant: "dot",
      color: "secondary",
      class: ["text-white", "border-white", "bg-transparent"],
    },
    {
      variant: "dot",
      color: "accent",
      class: ["text-base-900", "bg-transparent"],
    },
    {
      variant: "dot",
      color: "success",
      class: ["bg-transparent"],
    },
    {
      variant: "dot",
      color: "warning",
      class: ["bg-transparent"],
    },
    {
      variant: "dot",
      color: "danger",
      class: ["bg-transparent"],
    },
    // isOneChar / size
    {
      isOneChar: true,
      hasStartContent: false,
      hasEndContent: false,
      size: "sm",
      class: ["w-5 h-5 min-w-5 min-h-5"],
    },
    {
      isOneChar: true,
      hasStartContent: false,
      hasEndContent: false,
      size: "md",
      class: ["w-6 h-6 min-w-6 min-h-6"],
    },
    {
      isOneChar: true,
      hasStartContent: false,
      hasEndContent: false,
      size: "lg",
      class: ["w-7 h-7 min-w-7 min-h-7"],
    },
    // isOneChar / isCloseable
    {
      isOneChar: true,
      isCloseable: false,
      hasStartContent: false,
      hasEndContent: false,
      class: ["px-0 justify-center", "px-0 flex-none"],
    },
    {
      isOneChar: true,
      isCloseable: true,
      hasStartContent: false,
      hasEndContent: false,
      class: ["w-auto"],
    },
    // isOneChar / dot
    {
      isOneChar: true,
      variant: "dot",
      class: ["w-auto h-7 px-1 items-center", "px-2"],
    },
    // hasStartContent / size
    {
      hasStartContent: true,
      size: "sm",
      class: ["pl-0.5"],
    },
    {
      hasStartContent: true,
      size: ["md", "lg"],
      class: ["pl-1"],
    },
    // hasEndContent / size
    {
      hasEndContent: true,
      size: "sm",
      class: ["pr-0.5"],
    },
    {
      hasEndContent: true,
      size: ["md", "lg"],
      class: ["pr-1"],
    },
  ],
});

type ExtendedChipProps = React.ComponentProps<typeof ExtendedChip>;

type Variant = ExtendedChipProps["variant"];
type Color = ExtendedChipProps["color"];

export type ChipProps = Omit<ExtendedChipProps, "variant" | "color" | "id"> & {
  color?: Color;
  id: string;
  variant?: Variant;
};

export const Chip = forwardRef<HTMLDivElement, ChipProps>((props, ref) => (
  <ExtendedChip
    {...props}
    ref={ref as LegacyRef<ReactElement>}
    data-testid={`chip-${props.id}`}
    classNames={{
      closeButton: clsx("opacity-100 ring-transparent", {
        "focus:ring-offset-white": props.color === "primary",
        "focus:ring-offset-primary": props.color !== "primary",
      }),
    }}
  />
));

Chip.displayName = "RecareUI.Chip";
